/**
 * trial.js - the trial record (COMPONENT-JUDGEMENT.md section 4)
 *
 * WHAT THIS IS
 * A game knows the OUTCOME of a trial. To score 23 of the 208 boxes the engine
 * needs the SHAPE of it: when in the prompt sequence the child spoke, which
 * support rung was live at that moment, whether a hint had already fired, and
 * whether Pip had modelled the target. None of that is recoverable from a
 * transcript after the fact, so the game must write it down as it happens.
 *
 * This module defines that record, validates it, and derives the fields that
 * are a function of the others. It makes no judgements - judgement.js does
 * that, and it is the only consumer that has to exist.
 *
 * PURITY
 * Like framework.js, scoring.js and progression.js: no state, no DOM, no
 * network, no clock. Every timestamp arrives from the caller. That is what
 * makes the whole thing testable without a browser.
 *
 * ---------------------------------------------------------------------------
 * THE CLOCK
 *
 * Every timestamp in a record is milliseconds on ONE monotonic clock, chosen
 * by the game. `performance.now()` is the right one: it is monotonic, it is
 * millisecond-resolution, and it does not jump when the system clock is
 * corrected mid-trial.
 *
 * The epoch does not matter and is never checked, because only differences are
 * ever used. What IS checked is that a single record does not mix clocks:
 * `performance.now()` and `Date.now()` differ by roughly 1.7e12, so any record
 * whose timestamps span more than TRIAL_CLOCK_SPREAD_MS is rejected. That is a
 * cheap check for a bug that would otherwise produce a plausible-looking
 * onset latency of forty years.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS FROM THE SPEC AND WHAT IS NOT
 *
 * The fields listed in COMPONENT-JUDGEMENT.md section 4 are marked `spec: true`
 * in TRIAL_FIELDS. Eight more were added here, each because a judgement in
 * section 5 cannot be made without it; every one carries its reason in `why`.
 * Nothing else was added. A field nobody judges from is a field a game has to
 * maintain correctly for no return.
 * ========================================================================= */

import { SUPPORT_BY_ID, SUPPORT_LADDER } from './framework.js';

/** Widest plausible spread of timestamps within one trial record. */
export const TRIAL_CLOCK_SPREAD_MS = 60 * 60 * 1000;

/**
 * How long after the prompt ends an utterance still counts as a response to
 * it. ENGINE DEFAULT - the spec sets no number. Overridable per record via
 * `responseWindowMs`, and derived from `trialEndedAt` when that is supplied.
 */
export const RESPONSE_WINDOW_DEFAULT_MS = 5000;

/**
 * Minimum sustained duration for a vocalisation to count as an attempt.
 * COMPONENT-JUDGEMENT.md section 5, Phase 1 B: "sustained >= 150 ms".
 */
export const MIN_VOCALISATION_MS = 150;

/**
 * Where speechStartedAt came from, best first.
 *
 *   speech_onset        the recogniser's onspeechstart. What section 4 asks for.
 *   audio_energy        the first qualifying vocalisation from the RMS envelope.
 *                       Slightly late (it is reported when the sound ends) but
 *                       real, and the only source for a child who makes no word.
 *   inferred_from_final the arrival time of the final recognition result. LATE,
 *                       by hundreds of milliseconds, which costs the child boxes
 *                       rather than awarding them. A device that offers neither
 *                       of the above; recorded so a chart can be filtered on it.
 */
export const ONSET_SOURCES = Object.freeze(['speech_onset', 'audio_energy', 'inferred_from_final']);

/* =========================================================================
 * FIELD SPECIFICATION
 *
 * `kind` drives validation. `required` fields must be present on every record
 * regardless of capability; `requiredFor` names the capability that makes a
 * field mandatory (a game declaring prompt_sequence and then omitting the
 * support sequence has not instrumented anything).
 * ========================================================================= */

export const TRIAL_FIELDS = Object.freeze({
  // ---- the trial window -------------------------------------------------
  trialStartedAt: {
    kind: 'time', group: 'window', spec: false, required: true,
    why: 'Section 4 has no trial-open timestamp, but "did support escalate '
       + 'within the trial" and "was there a vocalisation in the response '
       + 'window" are both unanswerable without knowing when the trial began.',
  },
  promptEndedAt: {
    kind: 'time', group: 'window', spec: true, required: true,
    doc: 'Pip stopped speaking, or the prompt finished rendering.',
  },
  trialEndedAt: {
    kind: 'time', group: 'window', spec: false, required: true,
    why: 'Distinguishes "the child has not spoken yet" from "the child never '
       + 'spoke". Phase 1 B has to be able to record a definite absence.',
  },
  responseWindowMs: {
    kind: 'duration', group: 'window', spec: false, required: false, derived: true,
    why: 'The window an utterance must fall inside to count as a response to '
       + 'the prompt. Derived from trialEndedAt - promptEndedAt when absent.',
  },

  // ---- when the child spoke --------------------------------------------
  speechStartedAt: {
    kind: 'time', group: 'speech', spec: true, required: false, nullable: true,
    doc: 'onspeechstart - NOT the first interim result. null when the child '
       + 'did not speak. Interim results arrive hundreds of ms late and are '
       + 'gated on the recogniser deciding it heard a word, which is exactly '
       + 'the thing Phase 1 B must not depend on.',
  },
  finalAt: {
    kind: 'time', group: 'speech', spec: true, required: false, nullable: true,
    doc: 'When the final recognition result arrived.',
  },
  onsetLatencyMs: {
    kind: 'duration', group: 'speech', spec: true, required: false,
    derived: true, allowNegative: true,
    doc: 'speechStartedAt - promptEndedAt. Negative means the child spoke over '
       + 'the prompt, which is real data, not an error.',
  },
  onsetSource: {
    kind: 'onsetSource', group: 'speech', spec: false, required: false, nullable: true,
    why: 'WHICH signal produced speechStartedAt. A chart built on onsets '
       + 'inferred from the arrival of a final result is a different '
       + 'measurement from one built on the recogniser\'s own onspeechstart, '
       + 'and section 9.1 requires the chart to mean the same thing '
       + 'everywhere - so the record has to say which it was. Only the game '
       + 'knows: the engine cannot tell a vocalisation onset from an '
       + 'onspeechstart after the fact.',
  },

  // ---- what support was on screen --------------------------------------
  supportSequence: {
    kind: 'supportSequence', group: 'support', spec: false, required: false,
    requiredFor: 'prompt_sequence',
    why: 'Section 4 records supportRung, a single value. Phase 2 A/B/C are '
       + 'only independent if the game escalates WITHIN the trial, so the '
       + 'engine needs the whole ladder walk with timestamps, not just the '
       + 'rung that happened to be live at onset. supportRung is derived '
       + 'from this.',
  },
  supportRung: {
    kind: 'supportRung', group: 'support', spec: true, required: false,
    derived: true, nullable: true,
    doc: 'SUPPORT_LADDER id live at speechStartedAt. Derived from '
       + 'supportSequence; may be supplied directly by a game that does not '
       + 'escalate, in which case it must agree with the sequence.',
  },
  nominalRung: {
    kind: 'supportRung', group: 'support', spec: false, required: false,
    derived: true, nullable: true,
    why: 'The rung the trial OPENED at - the level\'s intended prompt level. '
       + '"Escalated" means nothing without it.',
  },
  modelPlayed: {
    kind: 'boolean', group: 'support', spec: true, required: false, default: false,
    doc: 'Did Pip speak the target aloud during this trial?',
  },
  modelEndedAt: {
    kind: 'time', group: 'support', spec: true, required: false, nullable: true,
    doc: 'When that model finished. Needed to tell a model the child heard '
       + 'before speaking from one that played after.',
  },
  hintFired: {
    kind: 'boolean', group: 'support', spec: true, required: false, default: false,
    doc: 'Did the time-delay hint appear?',
  },
  hintFiredAt: {
    kind: 'time', group: 'support', spec: false, required: false, nullable: true,
    why: 'Same reason as modelEndedAt: a hint that fired after the child '
       + 'started speaking did not help them, and Phase 6 D would otherwise '
       + 'be scored against a child who beat the hint.',
  },
  optionsVisible: {
    kind: 'boolean', group: 'support', spec: true, required: false, default: false,
    doc: 'Were word/object options on screen?',
  },
  modelScheduled: {
    kind: 'boolean', group: 'support', spec: false, required: false, default: false,
    why: 'Whether this trial\'s prompt sequence WOULD have modelled the target if '
       + 'the child had not answered first. Phase 3 D asks whether the child '
       + 'produced without echoing, and on a trial where a model was never on '
       + 'offer that question is free. The game reports the plan; the ENGINE '
       + 'decides whether the box is applicable (framework CONDITIONALS). '
       + 'Evidence, not a verdict - the same shape as optionsVisible.',
  },
  priorModelAgoMs: {
    kind: 'duration', group: 'support', spec: false, required: false, nullable: true,
    why: 'Milliseconds since this target was last modelled BEFORE this trial '
       + 'opened, or null if never. Phase 3 D is judged over THIS TRIAL '
       + '(framework, phase 3 component D note); a child modelled eight seconds '
       + 'ago on the previous trial may still be echoing, and that is real. It '
       + 'is recorded here, beside the box, and deliberately not folded into it '
       + '- one field, one question.',
  },

  // ---- the utterance -----------------------------------------------------
  transcript: {
    kind: 'string', group: 'utterance', spec: true, required: false, nullable: true,
    doc: 'null when nothing was recognised. A trial with no transcript is a '
       + 'valid, scoreable trial - see the Phase 1 B note in judgement.js.',
  },
  confidence: {
    kind: 'unitInterval', group: 'utterance', spec: true, required: false, nullable: true,
    doc: 'result[0].confidence. null when the browser did not report one.',
  },
  alternatives: {
    kind: 'alternatives', group: 'utterance', spec: true, required: false, default: [],
    doc: 'The other hypotheses maxAlternatives asked for.',
  },
  utteranceIndex: {
    kind: 'integer', group: 'utterance', spec: true, required: false, default: 0, min: 0,
    doc: 'nth final result within this trial. Continuous recognition emits one '
       + 'per pause, so a Phase 7 three-step command arrives as three.',
  },
  gapBeforeMs: {
    kind: 'duration', group: 'utterance', spec: true, required: false, nullable: true,
    doc: 'Silence since the previous final result. Section 4.3 distinguishes '
       + '"pause allowed" from "one utterance" on exactly this number.',
  },

  // ---- audio energy (capability: audio_energy) ---------------------------
  audio: {
    kind: 'audio', group: 'audio', spec: false, required: false, nullable: true,
    requiredFor: 'audio_energy',
    why: 'Section 4 predates the section 7.2 audio path. Phase 1 B asks '
       + 'whether the child made ANY sound; speech recognition returns words '
       + 'or nothing, so this is the only field that can answer it.',
  },

  // ---- the situation -----------------------------------------------------
  targetWord: {
    kind: 'string', group: 'situation', spec: false, required: false, nullable: true,
    why: 'targetObject is the object the game wants placed; targetWord is the '
       + 'string the child is expected to say. They differ as soon as a level '
       + 'asks for a phrase. Every "produced the target" judgement matches '
       + 'against this, falling back to targetObject.',
  },
  targetObject: {
    kind: 'string', group: 'situation', spec: true, required: false, nullable: true,
  },
  targetPosition: {
    kind: 'string', group: 'situation', spec: true, required: false, nullable: true,
  },
  legalMoves: {
    kind: 'array', group: 'situation', spec: true, required: false, default: [],
  },
  gridState: {
    kind: 'any', group: 'situation', spec: true, required: false, nullable: true,
  },
  attemptNumber: {
    kind: 'integer', group: 'situation', spec: true, required: true, min: 1,
    doc: 'Section 7.4 counts FIRST-attempt successes only. Getting this wrong '
       + 'silently corrupts the chart.',
  },
});

export const TRIAL_FIELD_NAMES = Object.freeze(Object.keys(TRIAL_FIELDS));

/** Fields quoted verbatim from COMPONENT-JUDGEMENT.md section 4. */
export const SPEC_FIELD_NAMES = Object.freeze(
  TRIAL_FIELD_NAMES.filter((n) => TRIAL_FIELDS[n].spec)
);

/** Fields this module added, each with a `why`. */
export const EXTENSION_FIELD_NAMES = Object.freeze(
  TRIAL_FIELD_NAMES.filter((n) => !TRIAL_FIELDS[n].spec)
);

/**
 * The fields a capability obliges a game to record.
 * @param {string} capabilityId
 * @returns {string[]}
 */
export function fieldsRequiredFor(capabilityId) {
  return TRIAL_FIELD_NAMES.filter((n) => TRIAL_FIELDS[n].requiredFor === capabilityId);
}

/* =========================================================================
 * SMALL HELPERS
 * ========================================================================= */

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);
const isNil = (v) => v === undefined || v === null;

/** Index of a support rung on the ladder, or null. Lower index = more support. */
export function supportIndexOf(rungId) {
  const rung = SUPPORT_BY_ID[rungId];
  return rung ? rung.index : null;
}

/**
 * Which rung was live at time `t`.
 *
 * Before the first entry the trial had not opened yet, so the first rung is
 * returned rather than null - a game cannot show nothing at all.
 *
 * @param {Array<{rungId:string, at:number}>} sequence sorted, oldest first
 * @param {number|null} t
 * @returns {string|null}
 */
export function rungLiveAt(sequence, t) {
  if (!Array.isArray(sequence) || sequence.length === 0) return null;
  if (!isFiniteNumber(t)) return null;
  let live = sequence[0].rungId;
  for (const entry of sequence) {
    if (entry.at <= t) live = entry.rungId;
    else break;
  }
  return live;
}

/* =========================================================================
 * VALIDATION
 * ========================================================================= */

function checkTime(name, value, errors) {
  if (!isFiniteNumber(value)) {
    errors.push(`${name} must be a finite number of milliseconds, got ${typeof value}.`);
    return false;
  }
  if (value < 0) {
    errors.push(`${name} must not be negative (${value}).`);
    return false;
  }
  return true;
}

function checkVocalisation(v, i, errors) {
  const at = `audio.vocalisations[${i}]`;
  if (typeof v !== 'object' || v === null) {
    errors.push(`${at} must be an object.`);
    return null;
  }
  if (!checkTime(`${at}.startedAt`, v.startedAt, errors)) return null;
  if (!checkTime(`${at}.endedAt`, v.endedAt, errors)) return null;
  if (v.endedAt < v.startedAt) {
    errors.push(`${at}.endedAt (${v.endedAt}) precedes startedAt (${v.startedAt}).`);
    return null;
  }
  const durationMs = isFiniteNumber(v.durationMs) ? v.durationMs : v.endedAt - v.startedAt;
  return {
    startedAt: v.startedAt,
    endedAt: v.endedAt,
    durationMs,
    peakRms: isFiniteNumber(v.peakRms) ? v.peakRms : null,
    meanRms: isFiniteNumber(v.meanRms) ? v.meanRms : null,
    ...(isFiniteNumber(v.noiseFloor) ? { noiseFloor: v.noiseFloor } : {}),
  };
}

/**
 * Validate and normalise one trial record.
 *
 * Errors mean the record cannot be judged. Warnings mean it can, but something
 * in it is worth a human looking at - a child who spoke over the prompt, a
 * support rung that was never escalated on a level that says it should be.
 *
 * @param {object} record
 * @param {object} [opts]
 * @param {Object<string,boolean>|string[]} [opts.requires] capabilities the
 *        game declared; their fields become mandatory.
 * @returns {{ok:boolean, errors:string[], warnings:string[],
 *            normalised:object|null}}
 */
export function validateTrialRecord(record, opts = {}) {
  const errors = [];
  const warnings = [];

  if (typeof record !== 'object' || record === null || Array.isArray(record)) {
    return { ok: false, errors: ['A trial record must be an object.'], warnings, normalised: null };
  }

  const requires = Array.isArray(opts.requires)
    ? opts.requires
    : Object.entries(opts.requires || {}).filter(([, on]) => on).map(([id]) => id);

  // ---- unknown fields are a typo, not an extension ----------------------
  for (const key of Object.keys(record)) {
    if (!TRIAL_FIELDS[key]) {
      errors.push(
        `Unknown trial field "${key}". Valid fields: ${TRIAL_FIELD_NAMES.join(', ')}.`
      );
    }
  }

  const out = {};

  // ---- required presence -------------------------------------------------
  for (const name of TRIAL_FIELD_NAMES) {
    const spec = TRIAL_FIELDS[name];
    const mandatory = spec.required
      || (spec.requiredFor && requires.includes(spec.requiredFor));
    if (mandatory && isNil(record[name])) {
      errors.push(
        spec.requiredFor && !spec.required
          ? `Trial field "${name}" is required because this game declares the `
            + `"${spec.requiredFor}" capability. ${spec.why || ''}`.trim()
          : `Trial field "${name}" is required.`
      );
    }
  }

  // ---- per-field checks --------------------------------------------------
  for (const name of TRIAL_FIELD_NAMES) {
    const spec = TRIAL_FIELDS[name];
    const value = record[name];

    if (isNil(value)) {
      out[name] = 'default' in spec ? spec.default : null;
      continue;
    }

    switch (spec.kind) {
      case 'time':
        if (checkTime(name, value, errors)) out[name] = value;
        break;

      case 'duration':
        if (!isFiniteNumber(value)) {
          errors.push(`${name} must be a finite number of milliseconds.`);
        } else if (value < 0 && !spec.allowNegative) {
          errors.push(`${name} must not be negative (${value}).`);
        } else {
          out[name] = value;
        }
        break;

      case 'boolean':
        if (typeof value !== 'boolean') errors.push(`${name} must be a boolean.`);
        else out[name] = value;
        break;

      case 'string':
        if (typeof value !== 'string') errors.push(`${name} must be a string.`);
        else out[name] = value;
        break;

      case 'integer':
        if (!Number.isInteger(value)) errors.push(`${name} must be an integer.`);
        else if (spec.min !== undefined && value < spec.min) {
          errors.push(`${name} must be >= ${spec.min}, got ${value}.`);
        } else out[name] = value;
        break;

      case 'unitInterval':
        if (!isFiniteNumber(value) || value < 0 || value > 1) {
          errors.push(`${name} must be a number between 0 and 1.`);
        } else out[name] = value;
        break;

      case 'array':
        if (!Array.isArray(value)) errors.push(`${name} must be an array.`);
        else out[name] = value;
        break;

      case 'onsetSource':
        if (!ONSET_SOURCES.includes(value)) {
          errors.push(
            `${name} "${value}" is not one of: ${ONSET_SOURCES.join(', ')}.`
          );
        } else out[name] = value;
        break;

      case 'supportRung':
        if (!SUPPORT_BY_ID[value]) {
          errors.push(
            `${name} "${value}" is not a SUPPORT_LADDER rung. Valid: `
            + SUPPORT_LADDER.map((r) => r.id).join(', ') + '.'
          );
        } else out[name] = value;
        break;

      case 'supportSequence': {
        if (!Array.isArray(value) || value.length === 0) {
          errors.push('supportSequence must be a non-empty array of { rungId, at }.');
          break;
        }
        const entries = [];
        value.forEach((entry, i) => {
          if (typeof entry !== 'object' || entry === null) {
            errors.push(`supportSequence[${i}] must be an object.`);
            return;
          }
          if (!SUPPORT_BY_ID[entry.rungId]) {
            errors.push(`supportSequence[${i}].rungId "${entry.rungId}" is not a SUPPORT_LADDER rung.`);
            return;
          }
          if (!checkTime(`supportSequence[${i}].at`, entry.at, errors)) return;
          entries.push({ rungId: entry.rungId, at: entry.at });
        });
        entries.sort((a, b) => a.at - b.at);
        out.supportSequence = entries;
        break;
      }

      case 'alternatives': {
        if (!Array.isArray(value)) {
          errors.push('alternatives must be an array.');
          break;
        }
        const alts = [];
        value.forEach((a, i) => {
          if (typeof a !== 'object' || a === null || typeof a.transcript !== 'string') {
            errors.push(`alternatives[${i}] must be { transcript, confidence }.`);
            return;
          }
          const c = a.confidence;
          if (!isNil(c) && (!isFiniteNumber(c) || c < 0 || c > 1)) {
            errors.push(`alternatives[${i}].confidence must be null or between 0 and 1.`);
            return;
          }
          alts.push({ transcript: a.transcript, confidence: isNil(c) ? null : c });
        });
        out.alternatives = alts;
        break;
      }

      case 'audio': {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
          errors.push('audio must be an object of { noiseFloor, vocalisations, envelope }.');
          break;
        }
        if (!Array.isArray(value.vocalisations)) {
          errors.push('audio.vocalisations must be an array (empty means "monitored, heard nothing").');
          break;
        }
        const vocs = [];
        value.vocalisations.forEach((v, i) => {
          const norm = checkVocalisation(v, i, errors);
          if (norm) vocs.push(norm);
        });
        vocs.sort((a, b) => a.startedAt - b.startedAt);

        let envelope = [];
        if (!isNil(value.envelope)) {
          if (!Array.isArray(value.envelope)) {
            errors.push('audio.envelope must be an array of { t, rms }.');
          } else {
            envelope = value.envelope.filter(
              (s) => s && isFiniteNumber(s.t) && isFiniteNumber(s.rms)
            ).map((s) => ({ t: s.t, rms: s.rms }));
            if (envelope.length !== value.envelope.length) {
              errors.push('audio.envelope entries must each be { t:number, rms:number }.');
            }
          }
        }

        out.audio = {
          noiseFloor: isFiniteNumber(value.noiseFloor) ? value.noiseFloor : null,
          monitored: value.monitored === undefined ? true : Boolean(value.monitored),
          vocalisations: vocs,
          envelope,
        };
        break;
      }

      case 'any':
      default:
        out[name] = value;
        break;
    }
  }

  if (errors.length) return { ok: false, errors, warnings, normalised: null };

  // ---- one clock ---------------------------------------------------------
  const stamps = [
    out.trialStartedAt, out.promptEndedAt, out.trialEndedAt,
    out.speechStartedAt, out.finalAt, out.modelEndedAt, out.hintFiredAt,
    ...(out.supportSequence || []).map((e) => e.at),
    ...((out.audio && out.audio.vocalisations) || []).flatMap((v) => [v.startedAt, v.endedAt]),
  ].filter(isFiniteNumber);

  const lo = Math.min(...stamps);
  const hi = Math.max(...stamps);
  if (hi - lo > TRIAL_CLOCK_SPREAD_MS) {
    errors.push(
      `Trial timestamps span ${Math.round((hi - lo) / 1000)}s, which is longer than a `
      + 'trial can be. This is almost always two clocks in one record - '
      + 'performance.now() somewhere and Date.now() somewhere else. Use one.'
    );
    return { ok: false, errors, warnings, normalised: null };
  }

  // ---- ordering ----------------------------------------------------------
  if (out.promptEndedAt < out.trialStartedAt) {
    errors.push('promptEndedAt precedes trialStartedAt.');
  }
  if (out.trialEndedAt < out.promptEndedAt) {
    errors.push('trialEndedAt precedes promptEndedAt.');
  }
  if (isFiniteNumber(out.speechStartedAt) && isFiniteNumber(out.finalAt)
      && out.finalAt < out.speechStartedAt) {
    errors.push('finalAt precedes speechStartedAt.');
  }
  if (out.modelPlayed === false && isFiniteNumber(out.modelEndedAt)) {
    errors.push('modelEndedAt is set but modelPlayed is false.');
  }
  if (out.hintFired === false && isFiniteNumber(out.hintFiredAt)) {
    errors.push('hintFiredAt is set but hintFired is false.');
  }
  if (out.onsetSource && !isFiniteNumber(out.speechStartedAt)) {
    errors.push('onsetSource names where speechStartedAt came from, but there is no speechStartedAt.');
  }
  if (out.onsetSource === 'audio_energy' && !(out.audio && out.audio.monitored)) {
    errors.push('onsetSource is "audio_energy" but audio was not monitored on this trial.');
  }

  if (errors.length) return { ok: false, errors, warnings, normalised: null };

  // ---- derived -----------------------------------------------------------
  if (out.responseWindowMs === null) {
    out.responseWindowMs = out.trialEndedAt > out.promptEndedAt
      ? out.trialEndedAt - out.promptEndedAt
      : RESPONSE_WINDOW_DEFAULT_MS;
  }

  out.onsetLatencyMs = isFiniteNumber(out.speechStartedAt)
    ? out.speechStartedAt - out.promptEndedAt
    : null;

  // A game that never escalates may send supportRung alone. Synthesise the
  // one-entry sequence so every consumer reads the same shape.
  if (!out.supportSequence && out.supportRung) {
    out.supportSequence = [{ rungId: out.supportRung, at: out.trialStartedAt }];
  }

  if (out.supportSequence) {
    out.nominalRung = out.supportSequence[0].rungId;
    const live = rungLiveAt(out.supportSequence, out.speechStartedAt);
    if (out.supportRung && live && out.supportRung !== live) {
      // Two different assertions about the same fact. Refusing is right: the
      // engine cannot know which one the game meant, and Phase 2 A/B/C all
      // hinge on it.
      errors.push(
        `supportRung "${out.supportRung}" disagrees with supportSequence, which has `
        + `"${live}" live at speechStartedAt (${out.speechStartedAt}).`
      );
    }
    out.supportRung = out.supportRung || live || out.nominalRung;
    const nominalIdx = supportIndexOf(out.nominalRung);
    const liveIdx = supportIndexOf(out.supportRung);
    // The ladder runs most-support (0) to least (7), so escalating support
    // moves DOWN the index.
    out.escalated = liveIdx !== null && nominalIdx !== null && liveIdx < nominalIdx;
    out.escalationSteps = out.escalated ? nominalIdx - liveIdx : 0;
  } else {
    out.nominalRung = null;
    out.escalated = false;
    out.escalationSteps = 0;
  }

  if (errors.length) return { ok: false, errors, warnings, normalised: null };

  /*
   * WHAT THE DEVICE ACTUALLY DELIVERED, as opposed to what the game declared.
   *
   * A capability is declared per BUILD, in the registry, and cannot be
   * asserted per request - that is deliberate. But it is DELIVERED per device
   * and per session: a game that instruments audio energy still meets a
   * microphone the parent declined, and the difference must not be invisible.
   * A trial with no monitoring is not a trial in which the child made no
   * sound.
   *
   * Derived, never supplied. `audio.monitored` and the presence of a support
   * sequence are facts already in the record, so there is nothing here a game
   * could get wrong or overstate.
   */
  out.delivered = Object.freeze({
    audio_energy: Boolean(out.audio && out.audio.monitored),
    prompt_sequence: Boolean(out.supportSequence && out.supportSequence.length),
  });

  out.respondedWithinWindow = out.onsetLatencyMs !== null
    && out.onsetLatencyMs >= 0
    && out.onsetLatencyMs <= out.responseWindowMs;

  out.spoke = isFiniteNumber(out.speechStartedAt);

  // ---- warnings: valid, but worth seeing ---------------------------------
  if (out.onsetLatencyMs !== null && out.onsetLatencyMs < 0) {
    warnings.push(
      `The child started speaking ${-out.onsetLatencyMs}ms before the prompt finished. `
      + 'Real data, but it is not a response to the prompt and will not score as one.'
    );
  }
  if (out.spoke && !out.respondedWithinWindow && out.onsetLatencyMs > 0) {
    warnings.push(
      `Speech onset was ${out.onsetLatencyMs}ms after the prompt, outside the `
      + `${out.responseWindowMs}ms response window.`
    );
  }
  if (out.spoke && !out.onsetSource) {
    warnings.push(
      'speechStartedAt is set but onsetSource is not. Record which signal it came '
      + 'from: a chart cannot otherwise be filtered on how the onset was measured.'
    );
  }
  if (out.transcript && !out.spoke) {
    warnings.push(
      'A transcript was recorded but speechStartedAt is null. Wire onspeechstart: '
      + 'without it, onset latency and Phase 9 A cannot be measured, and a '
      + 'vocalisation that produced no word is invisible.'
    );
  }
  if (out.audio && out.audio.monitored && out.audio.noiseFloor === null) {
    warnings.push('audio.noiseFloor is absent; vocalisation thresholds cannot be audited.');
  }

  return { ok: true, errors, warnings, normalised: Object.freeze(out) };
}

/**
 * Validate and return the normalised record, or throw.
 * For call sites that have already validated and want the shape.
 *
 * @param {object} record
 * @param {object} [opts] as validateTrialRecord
 * @returns {object} frozen normalised record
 */
export function normaliseTrialRecord(record, opts) {
  const result = validateTrialRecord(record, opts);
  if (!result.ok) {
    throw new Error('Invalid trial record:\n  ' + result.errors.join('\n  '));
  }
  return result.normalised;
}

/**
 * A minimal valid record, for tests and for a game finding its feet.
 * Everything optional is left at its default, so what a game must actually
 * fill in is visible at a glance.
 *
 * @param {object} [over] fields to override
 * @returns {object} an unvalidated record - pass it through validateTrialRecord
 */
export function blankTrialRecord(over = {}) {
  return {
    trialStartedAt: 0,
    promptEndedAt: 0,
    trialEndedAt: 0,
    attemptNumber: 1,
    ...over,
  };
}

/**
 * The vocalisations in a record that count as an attempt to vocalise:
 * sustained long enough, and starting inside the response window.
 *
 * COMPONENT-JUDGEMENT.md section 5, Phase 1 B: "RMS above a noise-floor
 * baseline, sustained >= 150 ms, inside the response window."
 *
 * @param {object} trial normalised record
 * @param {object} [opts]
 * @param {number} [opts.minDurationMs=MIN_VOCALISATION_MS]
 * @returns {Array<object>} the qualifying vocalisations, oldest first
 */
export function qualifyingVocalisations(trial, opts = {}) {
  const minDurationMs = opts.minDurationMs ?? MIN_VOCALISATION_MS;
  if (!trial || !trial.audio || !Array.isArray(trial.audio.vocalisations)) return [];

  const from = trial.promptEndedAt;
  const to = trial.promptEndedAt + trial.responseWindowMs;

  return trial.audio.vocalisations.filter(
    (v) => v.durationMs >= minDurationMs && v.startedAt >= from && v.startedAt <= to
  );
}

export default {
  ONSET_SOURCES,
  TRIAL_FIELDS,
  TRIAL_FIELD_NAMES,
  SPEC_FIELD_NAMES,
  EXTENSION_FIELD_NAMES,
  TRIAL_CLOCK_SPREAD_MS,
  RESPONSE_WINDOW_DEFAULT_MS,
  MIN_VOCALISATION_MS,
  fieldsRequiredFor,
  supportIndexOf,
  rungLiveAt,
  validateTrialRecord,
  normaliseTrialRecord,
  blankTrialRecord,
  qualifyingVocalisations,
};
